import { format } from 'date-fns';
import { memo, useMemo, useState } from 'react';

import { Tooltip } from '@air/primitive-tooltip';
import { parseAirDateToISO } from '@air/utilities';

import { CustomField } from '~/components/CustomField';
import { CustomFieldListWithOverflow } from '~/components/CustomFields/CustomFieldListWithOverflow';
import { customFieldIconMap } from '~/constants/customFieldIconMap';
import { WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT } from '~/constants/WorkspaceSpacing';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';

const INLINE_LABEL_MAX_WIDTH = 250;
const INLINE_LABEL_WIDTH_RATIO = 0.35;
const INLINE_LABEL_WIDTH_GUTTER = 16;
const OVERFLOW_FALLBACK_WIDTH = 200;
const OVERFLOW_WIDTH_GUTTER = 4;

type CustomFieldDisplayProps = {
  cfValue: AssignedCustomFieldValue;
  shouldShowLabelInline?: boolean;
};

export const CustomFieldDisplay = memo(({ cfValue, shouldShowLabelInline }: CustomFieldDisplayProps) => {
  const Icon = customFieldIconMap[cfValue.type.title];
  const [fullRowRef, setFullRowRef] = useState<HTMLDivElement | null>(null);
  const [valueRef, setValueRef] = useState<HTMLDivElement | null>(null);

  const labelWidth = useMemo(() => {
    if (!shouldShowLabelInline) {
      return 0;
    }
    if (fullRowRef) {
      return Math.min(
        INLINE_LABEL_MAX_WIDTH,
        fullRowRef.clientWidth * INLINE_LABEL_WIDTH_RATIO - INLINE_LABEL_WIDTH_GUTTER,
      );
    }

    return 0;
  }, [fullRowRef, shouldShowLabelInline]);

  const valueDisplay = useMemo(() => {
    if (cfValue.value) {
      return <CustomField field={cfValue.value} size="small" />;
    }
    if (cfValue.values?.length) {
      return (
        <CustomFieldListWithOverflow
          size="small"
          customFields={[cfValue]}
          allowedWidth={(valueRef?.clientWidth || OVERFLOW_FALLBACK_WIDTH) - OVERFLOW_WIDTH_GUTTER}
        />
      );
    }
    let text = cfValue.plainTextValue || '';

    if (cfValue.dateValue) {
      text = format(parseAirDateToISO(cfValue.dateValue), 'MMM dd, yyyy');
    }
    if (text) {
      return (
        <CustomField
          field={{
            id: cfValue.id,
            color: {
              name: 'light-grey',
            },
            value: text,
          }}
          size="small"
        />
      );
    }

    return <p className="ml-1 font-normal ads:text-metadata text-12 text-gray-8">None</p>;
  }, [cfValue, valueRef?.clientWidth]);

  return (
    <div
      className="flex w-full items-center gap-2 overflow-hidden"
      style={{
        height: WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT,
      }}
      ref={setFullRowRef}
    >
      <Icon className="size-3 shrink-0 ads:text-icon-subtle text-gray-8" />
      {shouldShowLabelInline ? (
        <div className="flex w-full items-center">
          <span
            className="truncate text-left font-medium ads:text-metadata text-12 text-gray-8"
            style={{ width: labelWidth }}
          >
            {cfValue.name}
          </span>
          <div className="flex flex-1" ref={setValueRef}>
            {valueDisplay}
          </div>
        </div>
      ) : (
        <div className="flex grow" ref={setValueRef}>
          <Tooltip label={cfValue.name} side="top" sideOffset={2}>
            <div className="inline-flex">{valueDisplay}</div>
          </Tooltip>
        </div>
      )}
    </div>
  );
});

CustomFieldDisplay.displayName = 'CustomFieldDisplay';
